import { FormCard, FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { DatePicker, Form, Modal, Radio, Select } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { NAMESPACE } from 'locale'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useUpdateAdministrationRecord } from '../../hooks/useUpdateAdministrationRecord'
import { AdministrationStatus, type StockRecord } from '../../types'
import { getAdministrationStatusOptions } from '../../utils/administrationUtils'

type BackSignMissedMedicationModalProps = {
  open: boolean
  medicationName: string
  record: StockRecord | undefined
  roundTime?: string
  dose?: string
  altDose?: string
  onCancel: () => void
  onSuccess?: () => void
}

const BackSignMissedMedicationModal = ({
  open,
  medicationName,
  record,
  roundTime,
  dose,
  altDose,
  onCancel,
  onSuccess
}: BackSignMissedMedicationModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()
  const { groupedCarerOptions } = useGroupedCarers()
  const sendEvent = useMixpanelEvent()

  const statusOptions = getAdministrationStatusOptions(t)
  const selectedOutcome = Form.useWatch('outcome', form)
  const hasDoseOptions = !!dose && !!altDose
  const isGivenOutcome = selectedOutcome === AdministrationStatus.GIVEN
  const showDosePicker = isGivenOutcome && hasDoseOptions
  const showStockQuestion = !!selectedOutcome && !isGivenOutcome

  const initialValues = useMemo(
    () => ({
      timeOffered: roundTime ? dayjs(roundTime) : undefined
    }),
    [roundTime]
  )

  const { updateAdministrationRecord, isLoading } =
    useUpdateAdministrationRecord({
      onSuccess: () => {
        showToast({ type: 'success', title: t('BACK_SIGN_SUCCESS') })
        onSuccess?.()
      },
      onError: () => {
        showToast({ type: 'error', title: t('BACK_SIGN_ERROR') })
      }
    })

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        if (!record?.id) {
          return
        }
        const isGiven = values.outcome === AdministrationStatus.GIVEN

        let doseGivenOption: string | undefined
        let stockAffected: boolean | undefined

        if (isGiven) {
          doseGivenOption = hasDoseOptions ? values.doseGivenOption : undefined
        } else {
          if (values.stockChange === 'NO_CHANGE') {
            stockAffected = false
          } else {
            stockAffected = true
            doseGivenOption = values.stockChange
          }
        }

        updateAdministrationRecord({
          recordId: record.id,
          data: {
            administrationStatus: values.outcome,
            administeredTime: values.timeOffered.toISOString(),
            administeredById: values.administeredBy,
            reasonForLateSignature: values.reasonForLateSignature,
            note: values.comment || null,
            doseGivenOption,
            stockAffected,
            isBackSign: true
          }
        })
          .then(() => {
            sendEvent(MixpanelEvents.EMAR_V2_BACK_SIGN_USED, {
              eventDate: dayjs().toISOString(),
              missedRoundDateTime: roundTime ?? null,
              updatedOutcome: values.outcome
            })
          })
          .catch(() => {
            // error toast is shown by useUpdateAdministrationRecord's onError
          })
      })
      .catch(() => {
        // validation errors are rendered against the fields themselves
      })
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <ModalTitle>
            <Heading level={3}>{t('SIGN_FOR_MISSED_MEDICATION')}</Heading>
            {medicationName && (
              <MedicationSubtitle>{medicationName}</MedicationSubtitle>
            )}
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} initialValues={initialValues} preserve={false}>
        <ModalContentContainer>
          <FormCard level={2}>
            <FormItem
              name="outcome"
              label={<Text>{t('OUTCOME')}</Text>}
              required
              rules={[{ required: true, message: t('OUTCOME_REQUIRED') }]}
            >
              <StyledSelect placeholder={t('SELECT_OUTCOME')}>
                {statusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </StyledSelect>
            </FormItem>

            {showDosePicker && (
              <FormItem
                name="doseGivenOption"
                label={<Text>{t('HOW_MANY_ADMINISTERED')}</Text>}
                required
                rules={[{ required: true, message: t('DOSE_REQUIRED') }]}
              >
                <Radio.Group>
                  <DoseOptionsRow>
                    <DoseOption value={dose}>{dose}</DoseOption>
                    <DoseOption value={altDose}>{altDose}</DoseOption>
                  </DoseOptionsRow>
                </Radio.Group>
              </FormItem>
            )}

            {showStockQuestion && (
              <FormItem
                name="stockChange"
                label={<Text>{t('HAS_STOCK_AMOUNT_CHANGED')}</Text>}
                required
                rules={[
                  { required: true, message: t('STOCK_CHANGE_REQUIRED') }
                ]}
              >
                <Radio.Group style={{ width: '100%' }}>
                  <StockOptionsList>
                    {hasDoseOptions ? (
                      <>
                        <StockOption value={dose}>
                          {t('YES_REMOVE_FROM_STOCK', { quantity: dose })}
                        </StockOption>
                        <StockOption value={altDose}>
                          {t('YES_REMOVE_FROM_STOCK', { quantity: altDose })}
                        </StockOption>
                      </>
                    ) : (
                      <StockOption value={dose}>
                        {t('YES_REMOVE_FROM_STOCK', { quantity: dose })}
                      </StockOption>
                    )}
                    <StockOption value="NO_CHANGE">
                      {t('NO_STOCK_IS_THE_SAME')}
                    </StockOption>
                  </StockOptionsList>
                </Radio.Group>
              </FormItem>
            )}

            <FormItem
              name="timeOffered"
              label={<Text>{t('TIME_OFFERED')}</Text>}
              required
              rules={[{ required: true, message: t('TIME_OFFERED_REQUIRED') }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                showTime
                format="DD-MM-YYYY HH:mm"
                placeholder={t('SELECT_DATE')}
                disabledDate={(current) =>
                  current && current > dayjs().endOf('day')
                }
              />
            </FormItem>

            <FormItem
              name="administeredBy"
              label={<Text>{t('ADMINISTERED_BY')}</Text>}
              required
              rules={[
                { required: true, message: t('ADMINISTERED_BY_REQUIRED') }
              ]}
            >
              <Select
                placeholder={t('SELECT_PERSON')}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {groupedCarerOptions.map((group) => (
                  <Select.OptGroup key={group.label} label={group.label}>
                    {group.options.map((option) => (
                      <Select.Option
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        {option.image ? (
                          <OptionContainer>
                            <ImageContainer>{option.image}</ImageContainer>
                            <StyledOptionText>{option.label}</StyledOptionText>
                          </OptionContainer>
                        ) : (
                          option.label
                        )}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </FormItem>

            <FormItem
              name="reasonForLateSignature"
              label={<Text>{t('REASON_FOR_LATE_SIGNATURE')}</Text>}
              required
              full
              rules={[
                {
                  required: true,
                  message: t('REASON_FOR_LATE_SIGNATURE_REQUIRED')
                },
                {
                  max: 250,
                  message: t('REASON_FOR_LATE_SIGNATURE_MAX_LENGTH')
                }
              ]}
            >
              <StyledTextArea
                rows={3}
                placeholder={t('ENTER_REASON')}
                maxLength={250}
                showCount
              />
            </FormItem>

            <FormItem
              name="comment"
              label={<Text>{t('COMMENT')}</Text>}
              full
              rules={[{ max: 150, message: t('COMMENT_MAX_LENGTH') }]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_COMMENT')}
                maxLength={150}
                showCount
              />
            </FormItem>
          </FormCard>
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="back-sign-cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
          isLoading={isLoading}
          data-testid="back-sign-save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
`

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  text-transform: none;
  font-weight: normal;
`

const MedicationSubtitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-top: 2px;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledSelect = styled(Select)`
  width: 100%;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledOptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const DoseOptionsRow = styled.div`
  display: flex;
  gap: 8px;
`

const DoseOption = styled(Radio)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  border-radius: 6px;
  font-weight: 500;

  &.ant-radio-wrapper-checked {
    border-color: ${({ theme }) => theme.color.status.primary};
    background-color: ${({ theme }) => theme.color.status.primary}0D;
  }
`

const StockOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const StockOption = styled(Radio)`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  border-radius: 6px;
  width: 100%;
  margin: 0;

  &::after {
    display: none;
  }

  .ant-radio {
    margin-left: auto;
  }

  &.ant-radio-wrapper-checked {
    border-color: ${({ theme }) => theme.color.status.primary};
    background-color: ${({ theme }) => theme.color.status.primary}0D;
  }
`

export default BackSignMissedMedicationModal
